import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import './App.css';

// Database utilities
const DB_NAME = 'BallparkDJ';
const DB_VERSION = 1;
const STORE_NAME = 'songs';

const initDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
  });
};

const saveToIndexedDB = async (songs) => {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    // Clear existing data
    await store.clear();

    // Save new data
    for (const song of songs) {
      await store.add(song);
    }
  } catch (error) {
    console.error('Failed to save to IndexedDB:', error);
  }
};

const clearIndexedDB = async () => {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    await store.clear();
    console.log('IndexedDB cleared successfully');
  } catch (error) {
    console.error('Failed to clear IndexedDB:', error);
  }
};

const clearServiceWorkerCache = async () => {
  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        await registration.unregister();
        console.log('Service worker unregistered');
      }
    }

    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
      console.log('All caches cleared');
    }
  } catch (error) {
    console.error('Failed to clear service worker cache:', error);
  }
};

const loadFromIndexedDB = async () => {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);

    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Failed to load from IndexedDB:', error);
    return [];
  }
};

// Styled Components
const AppContainer = styled.div`
  max-width: 800px;
  width: 100%;
  margin: 0 auto;
  padding: 20px;
  font-family: Arial, sans-serif;
  padding-bottom: 120px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 15px;
    padding-bottom: 140px;
    max-width: 100vw;
  }

  @media (max-width: 480px) {
    padding: 10px;
    padding-bottom: 120px;
  }
`;

const Title = styled.h1`
  text-align: center;
  color: #0a3161;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 30px;
  }
`;

const UploadSection = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 30px;
  margin-bottom: 30px;
  text-align: center;
  border: 2px dashed #dee2e6;
`;

const FileInput = styled.input`
  display: none;
`;

const UploadButton = styled.label`
  background-color: #007bff;
  color: white;
  padding: 12px 24px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 16px;
  display: inline-block;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #0056b3;
  }
`;

const SongsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding-top: 30px; /* Space for floating drag indicators */

  @media (max-width: 768px) {
    gap: 10px;
    padding-top: 35px;
  }

  @media (max-width: 480px) {
    gap: 8px;
    padding-top: 30px;
  }
`;

const SongCard = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isPlaying', 'isDragging', 'isDropZone'].includes(prop)
})`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 15px 15px 30px;
  background: ${props => {
    if (props.isDropZone) return 'linear-gradient(135deg, #e3f2fd, #f8f9fa)';
    if (props.isDragging) return 'linear-gradient(135deg, #e8f4fd, #f0f8ff)';
    return 'white';
  }};
  border-radius: 8px;
  box-shadow: ${props => {
    if (props.isDropZone) return '0 4px 12px rgba(33, 150, 243, 0.3), inset 0 0 0 2px #2196f3';
    if (props.isDragging) return '0 8px 25px rgba(33, 150, 243, 0.4), 0 4px 10px rgba(0, 0, 0, 0.1)';
    return '0 2px 4px rgba(0, 0, 0, 0.1)';
  }};
  border-left: 4px solid ${props => {
    if (props.isDropZone) return '#2196f3';
    if (props.isDragging) return '#1976d2';
    if (props.isPlaying) return '#28a745';
    return 'transparent';
  }};
  border-top: 1px solid ${props => {
    if (props.isDropZone) return '#2196f3';
    if (props.isDragging) return '#1976d2';
    return '#dee2e6';
  }};
  border-right: 1px solid ${props => {
    if (props.isDropZone) return '#2196f3';
    if (props.isDragging) return '#1976d2';
    return '#dee2e6';
  }};
  border-bottom: 1px solid ${props => {
    if (props.isDropZone) return '#2196f3';
    if (props.isDragging) return '#1976d2';
    return '#dee2e6';
  }};
  cursor: move;
  user-select: none;
  opacity: ${props => props.isDragging ? 0.9 : 1};
  transform: ${props => {
    if (props.isDropZone) return 'scale(1.02) translateY(-2px)';
    if (props.isDragging) return 'scale(1.05) translateY(-4px) rotate(1deg)';
    return 'none';
  }};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  min-height: 70px;
  max-width: 100%;
  overflow: visible; /* Allow floating indicators to show */
  z-index: ${props => props.isDragging ? 1000 : 1};

  &:before {
    content: '⋮⋮';
    position: absolute;
    left: 8px;
    top: 50%;
    transform: translateY(-50%);
    color: ${props => {
      if (props.isDropZone) return '#2196f3';
      if (props.isDragging) return '#1976d2';
      return '#999';
    }};
    font-size: ${props => props.isDragging ? '16px' : '14px'};
    line-height: 0.8;
    font-weight: bold;
    text-shadow: ${props => props.isDragging ? '0 2px 4px rgba(25, 118, 210, 0.3)' : 'none'};
    transition: all 0.2s ease;
  }

  /* Floating drag indicator for mobile */
  ${props => props.isDragging && `
    &:before {
      content: '✋ Dragging...';
      left: 50%;
      top: -25px;
      transform: translateX(-50%);
      background: #1976d2;
      color: white;
      padding: 4px 8px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 500;
      white-space: nowrap;
      text-shadow: none;
      z-index: 1001;

      @media (min-width: 769px) {
        content: '⋮⋮';
        left: 8px;
        top: 50%;
        transform: translateY(-50%);
        background: transparent;
        color: #1976d2;
        padding: 0;
        border-radius: 0;
        font-size: 16px;
        z-index: auto;
      }
    }
  `}

  &:after {
    content: ${props => props.isDropZone ? '"📍 Drop here"' : '""'};
    position: absolute;
    top: 50%;
    right: 15px;
    transform: translateY(-50%);
    background: #2196f3;
    color: white;
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    opacity: ${props => props.isDropZone ? 1 : 0};
    transition: opacity 0.2s ease;
    pointer-events: none;
    z-index: 10;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 12px;
    padding: 12px 12px 12px 25px;
    min-height: 60px;

    &:before {
      font-size: 12px;
      left: 6px;
    }

    &:after {
      right: 10px;
      font-size: 11px;
    }
  }

  @media (max-width: 480px) {
    padding: 10px 10px 10px 20px;
    gap: 10px;
    min-height: 50px;

    &:before {
      font-size: 10px;
      left: 4px;
    }

    &:after {
      right: 8px;
      font-size: 10px;
      padding: 3px 6px;
    }
  }
`;const SongInfo = styled.div`
  flex: 1;
`;

const SongName = styled.h3`
  margin: 0 0 8px 0;
  color: #333;
  font-size: 16px;
  line-height: 1.2;
  word-break: break-word;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;

  @media (max-width: 768px) {
    font-size: 15px;
    margin: 0 0 6px 0;
    white-space: normal;
    text-overflow: initial;
  }

  @media (max-width: 480px) {
    font-size: 14px;
    margin: 0 0 4px 0;
  }
`;

const SongSettings = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 6px;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    justify-content: center;
    gap: 12px;
    margin-top: 4px;
  }

  @media (max-width: 480px) {
    gap: 10px;
    margin-top: 2px;
  }
`;

const SettingGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;

  @media (max-width: 480px) {
    gap: 4px;
  }
`;

const SettingLabel = styled.span`
  font-size: 13px;
  color: #666;
  font-weight: 500;

  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;

  @media (max-width: 768px) {
    justify-content: center;
    gap: 6px;
  }

  @media (max-width: 480px) {
    gap: 4px;
  }
`;

const Button = styled.button`
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s ease;
  min-width: 60px;
  white-space: nowrap;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 480px) {
    padding: 5px 10px;
    font-size: 12px;
    min-width: 50px;
  }
`;

const PlayButton = styled(Button)`
  background-color: ${props => props.isPlaying ? '#dc3545' : '#28a745'};
  color: white;

  &:hover:not(:disabled) {
    background-color: ${props => props.isPlaying ? '#c82333' : '#218838'};
  }
`;

const FadeInButton = styled(Button)`
  background-color: #17a2b8;
  color: white;

  &:hover:not(:disabled) {
    background-color: #138496;
  }
`;

const FadeOutButton = styled(Button)`
  background-color: #ffc107;
  color: black;

  &:hover:not(:disabled) {
    background-color: #e0a800;
  }
`;

const EditButton = styled(Button)`
  background-color: #6c757d;
  color: white;

  &:hover:not(:disabled) {
    background-color: #5a6268;
  }
`;

const DeleteButton = styled(Button)`
  background-color: #dc3545;
  color: white;

  &:hover:not(:disabled) {
    background-color: #c82333;
  }
`;

const ClearAllButton = styled(Button)`
  background-color: #dc3545;
  color: white;
  margin: 20px auto;
  display: block;

  &:hover:not(:disabled) {
    background-color: #c82333;
  }
`;

const SaveButton = styled(Button)`
  background-color: #28a745;
  color: white;

  &:hover {
    background-color: #218838;
  }
`;

const CancelButton = styled(Button)`
  background-color: #6c757d;
  color: white;

  &:hover {
    background-color: #5a6268;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
  font-size: 18px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-top: 30px;
`;

const EditModal = styled.div`
  margin-top: 20px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #dee2e6;
`;

const EditRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  gap: 15px;

  &:last-child {
    margin-bottom: 0;
    justify-content: flex-end;
    gap: 10px;
  }
`;

const EditLabel = styled.label`
  font-weight: 500;
  color: #333;
  min-width: 150px;
`;

const EditInput = styled.input`
  padding: 6px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  width: 100px;
`;

// Toggle Switch Component
const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
`;

const ToggleSlider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${props => props.checked ? '#28a745' : '#ccc'};
  transition: 0.4s;
  border-radius: 24px;

  &:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: ${props => props.checked ? '29px' : '3px'};
    bottom: 3px;
    background-color: white;
    transition: 0.4s;
    border-radius: 50%;
  }
`;

const HiddenToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;
`;

const ConfirmDialog = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ConfirmContent = styled.div`
  background: white;
  padding: 30px;
  border-radius: 8px;
  text-align: center;
  max-width: 400px;
  margin: 20px;
`;

const ConfirmTitle = styled.h3`
  margin: 0 0 15px 0;
  color: #333;
`;

const ConfirmMessage = styled.p`
  margin: 0 0 25px 0;
  color: #666;
`;

const ConfirmButtons = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
`;

const App = () => {
  const [songs, setSongs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [playingSongId, setPlayingSongId] = useState(null);
  const [editingSongId, setEditingSongId] = useState(null);
  const [editData, setEditData] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmClearAll, setConfirmClearAll] = useState(false);
  const audioRef = useRef(null);

  // Load songs from IndexedDB on component mount
  useEffect(() => {
    const loadSongs = async () => {
      try {
        const savedSongs = await loadFromIndexedDB();

        if (savedSongs.length > 0) {
          // Add order field to existing songs that don't have it
          const songsWithOrder = savedSongs.map((song, index) => ({
            ...song,
            order: song.order ?? index // Use existing order or assign based on current index
          }));

          // Sort songs by order to maintain position
          songsWithOrder.sort((a, b) => a.order - b.order);

          setSongs(songsWithOrder);
        }
      } finally {
        // Set loading to false after processing is complete
        setIsLoading(false);
      }
    };

    loadSongs();
  }, []);

  // Save songs to IndexedDB whenever songs change
  useEffect(() => {
    if (songs.length > 0) {
      saveToIndexedDB(songs);
    }
  }, [songs]);

  // Register service worker
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('./sw.js')
        .then(registration => {
          console.log('SW registered: ', registration);
        })
        .catch(registrationError => {
          console.log('SW registration failed: ', registrationError);
        });
    }
  }, []);

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);

    files.forEach(file => {
      if (file.type.startsWith('audio/')) {
        // Convert file to base64 for offline storage
        const reader = new FileReader();
        reader.onload = (e) => {
          const songName = file.name.replace(/\.[^/.]+$/, "");

          const newSong = {
            id: Date.now() + Math.random(),
            data: e.target.result, // base64 data
            fileName: file.name,
            songName: songName,
            fileSize: file.size,
            startTime: 0,
            fadeIn: false,
            uploadDate: new Date().toISOString(),
            order: songs.length // Add at end of current list
          };

          setSongs(prev => [...prev, newSong]);
        };
        reader.readAsDataURL(file);
      }
    });

    event.target.value = '';
  };

  const playSong = (song, forceFadeIn = false) => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    // Handle both blob URLs and base64 data
    const audioSrc = song.data || song.url;
    audioRef.current = new Audio(audioSrc);

    const shouldFadeIn = forceFadeIn || song.fadeIn;
    const startTime = parseFloat(song.startTime) || 0;

    audioRef.current.addEventListener('loadedmetadata', () => {
      if (startTime > 0 && startTime < audioRef.current.duration) {
        audioRef.current.currentTime = startTime;
      }
    });

    const playAudio = () => {
      if (shouldFadeIn) {
        audioRef.current.volume = 0;
        audioRef.current.play().then(() => {
          let volume = 0;
          const fadeInterval = setInterval(() => {
            volume += 0.05;
            if (volume >= 1) {
              volume = 1;
              clearInterval(fadeInterval);
            }
            if (audioRef.current) {
              audioRef.current.volume = volume;
            }
          }, 100);
        }).catch(error => {
          console.error('Error playing audio:', error);
          setPlayingSongId(null);
        });
      } else {
        audioRef.current.volume = 1;
        audioRef.current.play().catch(error => {
          console.error('Error playing audio:', error);
          setPlayingSongId(null);
        });
      }
    };

    playAudio();

    audioRef.current.addEventListener('ended', () => {
      setPlayingSongId(null);
    });

    setPlayingSongId(song.id);
  };

  const togglePlayPause = (song) => {
    if (playingSongId === song.id) {
      // Currently playing, so pause
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingSongId(null);
    } else {
      // Not playing, so play
      playSong(song, false);
    }
  };

  const fadeOutCurrentSong = () => {
    if (audioRef.current && !audioRef.current.paused) {
      let volume = audioRef.current.volume;
      const fadeInterval = setInterval(() => {
        volume -= 0.05;
        if (volume <= 0) {
          volume = 0;
          clearInterval(fadeInterval);
          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
          }
          setPlayingSongId(null);
        }
        if (audioRef.current) {
          audioRef.current.volume = volume;
        }
      }, 100);
    }
  };

  const startEditing = (song) => {
    setEditingSongId(song.id);
    setEditData({
      startTime: song.startTime || 0,
      fadeIn: song.fadeIn || false
    });
  };

  const cancelEditing = () => {
    setEditingSongId(null);
    setEditData({});
  };

  const saveEditing = () => {
    setSongs(prev => prev.map(song =>
      song.id === editingSongId
        ? {
            ...song,
            startTime: parseFloat(editData.startTime) || 0,
            fadeIn: editData.fadeIn || false
          }
        : song
    ));
    setEditingSongId(null);
    setEditData({});
  };

  const confirmDeleteSong = (songId) => {
    const song = songs.find(s => s.id === songId);
    setConfirmDelete({ id: songId, name: song.songName });
  };

  const deleteSong = () => {
    const songId = confirmDelete.id;
    setSongs(prev => prev.filter(song => song.id !== songId));

    if (playingSongId === songId) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingSongId(null);
    }

    setEditingSongId(null);
    setConfirmDelete(null);
  };

  const requestClearAll = () => {
    setConfirmClearAll(true);
  };

  const clearAllSongs = async () => {
    try {
      // Stop any playing audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }

      // Clear all React state
      setSongs([]);
      setPlayingSongId(null);
      setEditingSongId(null);
      setEditData({});
      setConfirmDelete(null);
      setConfirmClearAll(false);
      setDraggedIndex(null);

      // Clear IndexedDB
      await clearIndexedDB();

      // Clear Service Worker caches
      await clearServiceWorkerCache();

      // Clear any remaining localStorage
      try {
        localStorage.clear();
        console.log('localStorage cleared');
      } catch (e) {
        console.warn('Could not clear localStorage:', e);
      }

      // Clear any remaining sessionStorage
      try {
        sessionStorage.clear();
        console.log('sessionStorage cleared');
      } catch (e) {
        console.warn('Could not clear sessionStorage:', e);
      }

      console.log('All data cleared successfully');

      // Optional: Reload page to ensure clean state
      // window.location.reload();

    } catch (error) {
      console.error('Error clearing all data:', error);
    }
  };

  const getSortedSongs = () => {
    return [...songs].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  };

  // Simple working drag and drop
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dropZoneIndex, setDropZoneIndex] = useState(null);
  const songsContainerRef = useRef(null);

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    setDropZoneIndex(null);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    if (draggedIndex !== null && index !== draggedIndex) {
      setDropZoneIndex(index);
    }
  };

  const handleDragLeave = (e) => {
    // Only clear drop zone if we're actually leaving the song card area
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setDropZoneIndex(null);
    }
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    setDropZoneIndex(null);

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const newSongs = [...songs];
    const draggedSong = newSongs[draggedIndex];

    // Remove dragged item
    newSongs.splice(draggedIndex, 1);

    // Insert at new position
    newSongs.splice(dropIndex, 0, draggedSong);

    // Update order fields to maintain consistent ordering
    const reorderedSongs = newSongs.map((song, index) => ({
      ...song,
      order: index
    }));

    setSongs(reorderedSongs);
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDropZoneIndex(null);
  };

  // Touch handlers for mobile drag and drop using native events
  useEffect(() => {
    const container = songsContainerRef.current;
    if (!container) return;

    let dragStarted = false;
    let draggedSongIndex = null;
    let startPos = { x: 0, y: 0 };

    const handleTouchStart = (e) => {
      const songCard = e.target.closest('[data-index]');
      if (!songCard) return;

      draggedSongIndex = parseInt(songCard.getAttribute('data-index'));
      const touch = e.touches[0];
      startPos = { x: touch.clientX, y: touch.clientY };
      dragStarted = false;
    };

    const handleTouchMove = (e) => {
      if (draggedSongIndex === null) return;

      const touch = e.touches[0];
      const deltaX = Math.abs(touch.clientX - startPos.x);
      const deltaY = Math.abs(touch.clientY - startPos.y);

      // Start dragging if moved significantly
      if (!dragStarted && (deltaX > 15 || deltaY > 15)) {
        dragStarted = true;
        setDraggedIndex(draggedSongIndex);

        // Prevent scrolling
        e.preventDefault();
      }

      if (dragStarted) {
        e.preventDefault(); // Continue preventing scroll during drag

        // Update drop zone indicator during drag
        const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);
        const targetCard = elementBelow?.closest('[data-index]');

        if (targetCard) {
          const hoverIndex = parseInt(targetCard.getAttribute('data-index'));
          if (hoverIndex !== draggedSongIndex) {
            setDropZoneIndex(hoverIndex);
          } else {
            setDropZoneIndex(null);
          }
        } else {
          setDropZoneIndex(null);
        }
      }
    };

    const handleTouchEnd = (e) => {
      if (!dragStarted || draggedSongIndex === null) {
        // Reset everything
        draggedSongIndex = null;
        dragStarted = false;
        setDraggedIndex(null);
        setDropZoneIndex(null);
        return;
      }

      // Get the element under the touch point
      const touch = e.changedTouches[0];
      const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);
      const targetCard = elementBelow?.closest('[data-index]');

      if (targetCard) {
        const dropIndex = parseInt(targetCard.getAttribute('data-index'));

        if (dropIndex !== draggedSongIndex) {
          const newSongs = [...songs];
          const draggedSong = newSongs[draggedSongIndex];

          // Remove from old position
          newSongs.splice(draggedSongIndex, 1);
          // Insert at new position
          newSongs.splice(dropIndex, 0, draggedSong);

          // Update order fields to maintain consistent ordering
          const reorderedSongs = newSongs.map((song, index) => ({
            ...song,
            order: index
          }));

          setSongs(reorderedSongs);
        }
      }

      // Reset everything
      draggedSongIndex = null;
      dragStarted = false;
      setDraggedIndex(null);
      setDropZoneIndex(null);
    };

    // Add event listeners with passive: false to allow preventDefault
    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: false });

    // Cleanup
    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [songs]); // Only depend on songs, not the drag state

  return (
    <AppContainer>
      <Title>🎵 Ballpark DJ</Title>

      {isLoading ? (
        <EmptyState>
          Loading your music library...
        </EmptyState>
      ) : songs.length === 0 ? (
        <EmptyState>
          No songs in your library yet.<br />
          Upload some audio files to get started!
        </EmptyState>
      ) : (
        <SongsList ref={songsContainerRef}>
          {getSortedSongs().map((song, index) => (
            <SongCard
              key={song.id}
              data-song-id={song.id}
              data-index={index}
              isPlaying={playingSongId === song.id}
              isDragging={draggedIndex === index}
              isDropZone={dropZoneIndex === index}
              draggable={true}
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={handleDragOver}
              onDragEnter={(e) => handleDragEnter(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
            >
              <SongInfo>
                <SongName>{song.songName}</SongName>
                <SongSettings>
                  <SettingGroup>
                    <SettingLabel>Start:</SettingLabel>
                    <span>{song.startTime || 0}s</span>
                  </SettingGroup>
                  <SettingGroup>
                    <SettingLabel>Fade In:</SettingLabel>
                    <span>{song.fadeIn ? 'Yes' : 'No'}</span>
                  </SettingGroup>
                </SongSettings>
              </SongInfo>

              <ButtonGroup>
                <PlayButton
                  isPlaying={playingSongId === song.id}
                  onClick={() => togglePlayPause(song)}
                >
                  {playingSongId === song.id ? '⏸️ Pause' : '▶️ Play'}
                </PlayButton>

                <FadeInButton
                  onClick={() => playSong(song, true)}
                  disabled={playingSongId === song.id}
                >
                  Fade In
                </FadeInButton>

                {playingSongId === song.id && (
                  <FadeOutButton onClick={fadeOutCurrentSong}>
                    Fade Out
                  </FadeOutButton>
                )}

                <EditButton
                  onClick={() => startEditing(song)}
                >
                  Edit
                </EditButton>
              </ButtonGroup>

              {editingSongId === song.id && (
                <EditModal>
                  <EditRow>
                    <EditLabel>Start Time (seconds):</EditLabel>
                    <EditInput
                      type="number"
                      step="0.1"
                      value={editData.startTime}
                      onChange={(e) => setEditData(prev => ({...prev, startTime: e.target.value}))}
                    />
                  </EditRow>
                  <EditRow>
                    <EditLabel>Fade In on Play:</EditLabel>
                    <ToggleSwitch>
                      <HiddenToggleInput
                        type="checkbox"
                        checked={editData.fadeIn}
                        onChange={(e) => setEditData(prev => ({...prev, fadeIn: e.target.checked}))}
                      />
                      <ToggleSlider checked={editData.fadeIn} />
                    </ToggleSwitch>
                  </EditRow>
                  <EditRow>
                    <SaveButton onClick={saveEditing}>Save</SaveButton>
                    <CancelButton onClick={cancelEditing}>Cancel</CancelButton>
                    <DeleteButton onClick={() => confirmDeleteSong(song.id)}>Delete Song</DeleteButton>
                  </EditRow>
                </EditModal>
              )}
            </SongCard>
          ))}
        </SongsList>
      )}

      <UploadSection>
        <FileInput
          type="file"
          accept="audio/*"
          multiple
          onChange={handleFileUpload}
          id="file-upload"
        />
        <UploadButton htmlFor="file-upload">
          Choose Audio Files
        </UploadButton>
      </UploadSection>

      {songs.length > 0 && (
        <ClearAllButton onClick={requestClearAll}>
          Clear All Songs & Data
        </ClearAllButton>
      )}

      {confirmClearAll && (
        <ConfirmDialog>
          <ConfirmContent>
            <ConfirmTitle>Clear All Data?</ConfirmTitle>
            <ConfirmMessage>
              Are you sure you want to clear ALL songs, IndexedDB data, and service worker cache?<br />
              <strong>This will permanently delete everything and cannot be undone.</strong>
            </ConfirmMessage>
            <ConfirmButtons>
              <DeleteButton onClick={clearAllSongs}>Yes, Clear Everything</DeleteButton>
              <CancelButton onClick={() => setConfirmClearAll(false)}>Cancel</CancelButton>
            </ConfirmButtons>
          </ConfirmContent>
        </ConfirmDialog>
      )}

      {confirmDelete && (
        <ConfirmDialog>
          <ConfirmContent>
            <ConfirmTitle>Delete Song?</ConfirmTitle>
            <ConfirmMessage>
              Are you sure you want to delete "{confirmDelete.name}"?<br />
              This action cannot be undone.
            </ConfirmMessage>
            <ConfirmButtons>
              <DeleteButton onClick={deleteSong}>Yes, Delete</DeleteButton>
              <CancelButton onClick={() => setConfirmDelete(null)}>Cancel</CancelButton>
            </ConfirmButtons>
          </ConfirmContent>
        </ConfirmDialog>
      )}
    </AppContainer>
  );
};

export default App;